import moment from 'moment-timezone'

import TimeZones from 'static/time_zones'
import { AlertActionTypes, AlertCheckTypes, AlertScheduleTypes } from 'static/constants'
import { validActiveWindow } from 'utils/time'
import { listOrEmpty } from 'utils/immutable'

export const timezoneOptions = TimeZones.map(t => ({ value: t, label: t }))

export const defaultTimezone = 'Europe/Berlin'

export const createDefaultActiveWindows = () => [{ from: '08:00', to: '18:00' }]

export const dayToggles = [1, 2, 3, 4, 5, 6, 7].map(value => ({
  value,
  label: moment().isoWeekday(value).format('dd')
}))

const templateFor = (scheduleType, checkType) => {
  if (scheduleType === AlertScheduleTypes.CALENDAR) {
    return 'news_report'
  }

  if (checkType === AlertCheckTypes.THRESHOLD) {
    return 'buzz_alert'
  }

  return 'news_alert'
}

export const defaultSchedule = (type, timezone = defaultTimezone) => {
  if (type === AlertScheduleTypes.CALENDAR) {
    return {
      type: AlertScheduleTypes.CALENDAR,
      times: ['08:00'],
      days: [1, 2, 3, 4, 5],
      timezone
    }
  }

  return {
    type: AlertScheduleTypes.INTERVAL,
    interval: 'PT10M',
    days: [1, 2, 3, 4, 5],
    activeWindows: [],
    timezone
  }
}

export const defaultCheck = type => {
  if (type === AlertCheckTypes.WITHOUT_CONDITION) {
    return null
  }

  if (type === AlertCheckTypes.THRESHOLD) {
    return {
      type: AlertCheckTypes.THRESHOLD,
      metric: { kind: 'news_count' },
      operator: 'gt',
      value: 50,
      window: 'calendar_day'
    }
  }

  return { type: AlertCheckTypes.NEW_HITS }
}

export const applyActionTemplates = alert => {
  const template = templateFor(
    alert.getIn(['schedule', 'type']),
    alert.getIn(['condition', 'checks', 0, 'type'])
  )

  return alert.set(
    'actions',
    listOrEmpty(alert.get('actions')).map(action => action.setIn(['content', 'html', 'template'], template))
  )
}

export const syncSavedSearchId = (alert, savedSearchId) => (
  alert
    .setIn(['condition', 'savedSearchId'], savedSearchId)
    .set(
      'actions',
      listOrEmpty(alert.get('actions')).map(action => action.set('savedSearchId', savedSearchId))
    )
)

export const getAlertValidationErrors = alert => {
  const scheduleType = alert.getIn(['schedule', 'type']) || AlertScheduleTypes.INTERVAL
  const checks = listOrEmpty(alert.getIn(['condition', 'checks']))
  const actions = listOrEmpty(alert.get('actions'))
  const conditionSavedSearchId = alert.getIn(['condition', 'savedSearchId'])
  const namePresent = (alert.get('nameEn') || '').trim().length > 0
  const savedSearchPresent = Boolean(conditionSavedSearchId)
  const times = listOrEmpty(alert.getIn(['schedule', 'times']))
  const activeWindows = listOrEmpty(alert.getIn(['schedule', 'activeWindows']))
  const scheduleTimesPresent = scheduleType !== AlertScheduleTypes.CALENDAR || times.size > 0
  const activeWindowsValid = scheduleType !== AlertScheduleTypes.INTERVAL
    || activeWindows.size === 0
    || (activeWindows.size === 1 && validActiveWindow(activeWindows.first()))
  const conditionAllowed = scheduleType === AlertScheduleTypes.CALENDAR || checks.size > 0
  const savedSearchAllowed = checks.size === 0 || savedSearchPresent
  const chartsValid = actions.every(action => (
    listOrEmpty(action.get('charts')).every(chart => Boolean(
      chart.getIn(['dataSourceOpts', 'savedSearchId'])
      || chart.get('savedSearchId')
      || conditionSavedSearchId
    ))
  ))
  const deliveryPresent = actions.size > 0
  const actionDeliveriesValid = actions.every(action => {
    if (action.get('type') === AlertActionTypes.WEBHOOK) {
      return (action.get('url') || '').trim().length > 0
    }

    return listOrEmpty(action.get('recipients')).size > 0 || listOrEmpty(action.get('mailingListIds')).size > 0
  })
  const errors = []

  if (!namePresent) {
    errors.push({ step: 'name', message: 'name_required' })
  }

  if (!conditionAllowed) {
    errors.push({ step: 'trigger', message: 'condition_required' })
  }

  if (!savedSearchAllowed) {
    errors.push({ step: 'trigger', message: 'saved_search_mandatory_for_condition' })
  }

  if (!chartsValid) {
    errors.push({ step: 'actions', message: 'chart_saved_search_required' })
  }

  if (!alert.get('enabled')) {
    return errors
  }

  if (!deliveryPresent) {
    errors.push({ step: 'actions', message: 'actions_required' })
  }

  if (!actionDeliveriesValid) {
    errors.push({ step: 'actions', message: 'delivery_required' })
  }

  if (!scheduleTimesPresent) {
    errors.push({ step: 'trigger', message: 'alert_times_required' })
  }

  if (!activeWindowsValid) {
    errors.push({ step: 'trigger', message: 'alert_active_window_error' })
  }

  return errors
}

export const isValidAlert = alert => getAlertValidationErrors(alert).length === 0
